import React from 'react';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@strivelabs/ui-core';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import { formatDate } from '@/utils/date';
import type { KeywordRowWithStatus } from './types';

export interface ContentOutlineTableProps {
  data: KeywordRowWithStatus[];
  renderAction: (row: KeywordRowWithStatus) => React.ReactNode;
  getInProgressBadge?: (row: KeywordRowWithStatus) => React.ReactNode;
}

const statusBadgeConfig = {
  [ProcessingStatus.PENDING]: {
    label: 'Todo',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
  },
  [ProcessingStatus.IN_PROGRESS]: {
    label: 'In Progress',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
  },
  [ProcessingStatus.WAITING_FOR_APPROVAL]: {
    label: 'Awaiting Approval',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700',
  },
  [ProcessingStatus.COMPLETED]: {
    label: 'Done',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
  },
  [ProcessingStatus.FAILED]: {
    label: 'Failed',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
  },
  [ProcessingStatus.DRAFT_GENERATING]: {
    label: 'Generating Draft',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
  },
  [ProcessingStatus.DRAFT_GENERATED]: {
    label: 'Draft Generated',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
  },
};

const FALLBACK_STATUS_CONFIG = {
  label: 'Unknown',
  bgColor: 'bg-gray-100',
  textColor: 'text-gray-700',
};

// Status Badge Component using Tailwind classes
export const StatusBadge: React.FC<{ status: ProcessingStatus; label?: React.ReactNode }> = ({ status, label }) => {
  const config = statusBadgeConfig[status] ?? FALLBACK_STATUS_CONFIG;
  return (
    <span
      className={`inline-flex items-center max-w-[200px] px-1.5 pt-px pb-0.5 rounded-badge text-xs leading-4 tracking-badge ${config.bgColor} ${config.textColor}`}
    >
      <span className="truncate">{label ?? config.label}</span>
    </span>
  );
};

export const ContentOutlineTable: React.FC<ContentOutlineTableProps> = ({ data, renderAction, getInProgressBadge }) => {
  return (
    <Table variant="default">
      <TableHeader variant="default">
        <TableRow variant="default">
          <TableHead variant="default" pinned="left" className="w-[220px]">
            Primary Keywords
          </TableHead>
          <TableHead variant="default" className="w-[220px]">
            Secondary Keywords
          </TableHead>
          <TableHead variant="default" className="w-[220px]">
            Status
          </TableHead>
          <TableHead variant="default" className="w-[97px]">
            Date
          </TableHead>
          <TableHead variant="default" className="w-[100px]">
            Action
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row) => (
          <TableRow key={row._id} variant="default">
            {/* Primary Keywords - joined by commas, with truncation and tooltip */}
            <TableCell variant="default" pinned="left" className="max-w-[220px]" title={(row.data.primary_keywords ?? []).join(', ') || 'N/A'}>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                <span className="text-sm leading-4 font-normal text-custom-text-primary tracking-table-cell">
                  {(row.data.primary_keywords ?? []).join(', ') || 'N/A'}
                </span>
              </div>
            </TableCell>

            {/* Secondary Keywords - joined by commas or "N/A", with truncation and tooltip */}
            <TableCell
              variant="default"
              className="max-w-[220px]"
              title={(row.data.secondary_keywords ?? []).length > 0 ? row.data.secondary_keywords.join(', ') : 'N/A'}
            >
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                <span className="text-sm leading-4 font-normal text-custom-text-primary tracking-table-cell">
                  {(row.data.secondary_keywords ?? []).length > 0 ? row.data.secondary_keywords.join(', ') : 'N/A'}
                </span>
              </div>
            </TableCell>

            {/* Status - badge with adjusted padding for vertical alignment */}
            <TableCell variant="default" className="pb-2 pt-[9px]">
              {row._status === ProcessingStatus.IN_PROGRESS && getInProgressBadge ? (
                getInProgressBadge(row) ?? <StatusBadge status={row._status} />
              ) : (
                <StatusBadge status={row._status} />
              )}
            </TableCell>

            {/* Date */}
            <TableCell variant="default">
              <span className="text-sm leading-4 font-normal text-custom-text-primary whitespace-nowrap tracking-table-cell">
                {formatDate(row.created_at)}
              </span>
            </TableCell>

            {/* Action - with light purple background and special border */}
            <TableCell variant="default" className="bg-action-bg border-b border-b-custom-border">
              <div className="text-sm leading-4 font-normal text-custom-text-primary tracking-table-cell">
                {renderAction(row)}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
